/* eslint-disable react-hooks/rules-of-hooks */
import { DEFAULT_DATA_HOOK_INTERVAL } from '@constants/dataHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

type DataHookConfig = {
  minimumInterval?: number;
  autoFetch?: boolean;
};

function DataHookFactory<DataType>(
  getter: (...params: any[]) => Promise<DataType>,
  {
    minimumInterval = DEFAULT_DATA_HOOK_INTERVAL,
    autoFetch = true,
  }: DataHookConfig,
) {
  return (_params?: any[], _interval?: number) => {
    const interval = useMemo(() => {
      if (_interval) return Math.max(minimumInterval, _interval);
      return undefined;
    }, [_interval]);
    const [error, setError] = useState<unknown>(undefined);
    const [data, setData] = useState<DataType | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const params = useMemo(() => {
      setLoading(true);
      return _params;
    }, [_params]);

    const fetch = useCallback(
      async (updateLoading?: boolean) => {
        if (updateLoading) setLoading(true);
        try {
          const fetchedData =
            params != null ? await getter(...params) : await getter();
          setData(fetchedData);
        } catch (err) {
          setError(err);
        } finally {
          setLoading(false);
        }
      },
      [params],
    );

    useEffect(() => {
      if (autoFetch) fetch();
      if (interval) {
        const loop = setInterval(fetch, interval);
        return () => clearInterval(loop);
      }
      return () => {};
    }, [interval, fetch]);

    return { data, loading, error, fetch, getter };
  };
}

export default DataHookFactory;
